import { useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  Battery,
  Box,
  Boxes,
  CheckSquare,
  Filter,
  Plus,
  SlidersHorizontal,
  Square,
} from 'lucide-react';
import { BosDurum, Buton, Rozet, VeriTablo, type ColumnDef, type RozetTonu } from '@emanetx/ui';
import type { Dolap, DolapBoyutu, DolapDurumu } from '@emanetx/sdk';
import { useDolapListesi } from '../dolaplar/api.js';
import { YeniDolapDialog } from '../dolaplar/yeni-dolap-dialog.js';
import { TopluUretDialog } from '../dolaplar/toplu-uret-dialog.js';
import { TopluDuzenleDialog } from '../dolaplar/toplu-duzenle-dialog.js';

const DURUM_TONU: Record<DolapDurumu, RozetTonu> = {
  MUSAIT: 'yesil',
  MESGUL: 'turuncu',
  REZERVE: 'mavi',
  BAKIM: 'gri',
  CEVRIMDISI: 'kirmizi',
};

const DURUM_ETIKET: Record<DolapDurumu, string> = {
  MUSAIT: 'Müsait',
  MESGUL: 'Meşgul',
  REZERVE: 'Rezerve',
  BAKIM: 'Bakım',
  CEVRIMDISI: 'Çevrimdışı',
};

const BOYUT_ETIKET: Record<DolapBoyutu, string> = {
  KUCUK: 'S',
  ORTA: 'M',
  BUYUK: 'L',
  EKSTRA: 'XL',
};

export function NoktaDolaplarPaneli({
  noktaId,
  noktaKod,
  noktaAd,
}: {
  noktaId: string;
  noktaKod: string;
  noktaAd: string;
}) {
  const [yeniAcik, setYeniAcik] = useState(false);
  const [topluAcik, setTopluAcik] = useState(false);
  const [duzenleAcik, setDuzenleAcik] = useState(false);
  const [sayfa, setSayfa] = useState(1);
  const [durum, setDurum] = useState<DolapDurumu | ''>('');
  const [seciliIdler, setSeciliIdler] = useState<Set<string>>(new Set());
  const navigate = useNavigate();

  const liste = useDolapListesi({
    noktaId,
    sayfa,
    boyut: 50,
    ...(durum && { durum: durum as DolapDurumu }),
  });

  const dolaplar = liste.data?.veriler ?? [];

  const dolabaTikla = (dolapId: string) => {
    void navigate({
      to: '/panel/noktalar/$id',
      params: { id: noktaId },
      search: { tab: 'dolaplar', dolap: dolapId },
    });
  };

  const birTikla = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const yeni = new Set(seciliIdler);
    if (yeni.has(id)) yeni.delete(id);
    else yeni.add(id);
    setSeciliIdler(yeni);
  };

  const hepsiSecili = dolaplar.length > 0 && dolaplar.every((d) => seciliIdler.has(d.id));
  const hicSecili = dolaplar.length > 0 && dolaplar.some((d) => seciliIdler.has(d.id));

  const hepsiSec = () => {
    if (hepsiSecili) {
      const yeni = new Set(seciliIdler);
      dolaplar.forEach((d) => yeni.delete(d.id));
      setSeciliIdler(yeni);
    } else {
      const yeni = new Set(seciliIdler);
      dolaplar.forEach((d) => yeni.add(d.id));
      setSeciliIdler(yeni);
    }
  };

  const kolonlar = useMemo<ColumnDef<Dolap, unknown>[]>(
    () => [
      {
        id: 'secim',
        header: () => (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              hepsiSec();
            }}
            aria-label="Hepsini seç"
            className="hover:text-marka-600 inline-flex p-0.5"
          >
            {hepsiSecili ? (
              <CheckSquare size={15} className="text-marka-600" />
            ) : hicSecili ? (
              <Square size={15} className="text-marka-600" />
            ) : (
              <Square size={15} className="text-fume-400" />
            )}
          </button>
        ),
        meta: { etiket: 'Seçim' },
        enableSorting: false,
        cell: ({ row }) => {
          const seciliMi = seciliIdler.has(row.original.id);
          return (
            <button
              type="button"
              onClick={(e) => birTikla(row.original.id, e)}
              aria-label="Seç"
              className="hover:text-marka-600 inline-flex p-0.5"
            >
              {seciliMi ? (
                <CheckSquare size={15} className="text-marka-600" />
              ) : (
                <Square size={15} className="text-fume-400" />
              )}
            </button>
          );
        },
      },
      {
        accessorKey: 'numara',
        header: 'No',
        meta: { etiket: 'No' },
        cell: ({ row }) => (
          <span className="text-marka-700 dark:text-marka-400 font-mono font-semibold">
            #{row.original.numara}
          </span>
        ),
      },
      {
        accessorKey: 'boyut',
        header: 'Boyut',
        meta: { etiket: 'Boyut' },
        cell: ({ row }) => <Rozet ton="fume">{BOYUT_ETIKET[row.original.boyut]}</Rozet>,
      },
      {
        accessorKey: 'durum',
        header: 'Durum',
        meta: { etiket: 'Durum' },
        cell: ({ row }) => (
          <Rozet ton={DURUM_TONU[row.original.durum]} nokta>
            {DURUM_ETIKET[row.original.durum]}
          </Rozet>
        ),
      },
      {
        accessorKey: 'cihazUid',
        header: 'Cihaz UID',
        meta: { etiket: 'Cihaz UID' },
        cell: ({ row }) =>
          row.original.cihazUid ? (
            <span className="text-fume-600 dark:text-fume-300 font-mono text-xs">
              {row.original.cihazUid}
            </span>
          ) : (
            <span className="text-fume-400 text-xs">—</span>
          ),
      },
      {
        accessorKey: 'pilMv',
        header: 'Pil',
        meta: { etiket: 'Pil' },
        cell: ({ row }) => <PilGosterge mv={row.original.pilMv ?? null} />,
        sortingFn: (a, b) => (a.original.pilMv ?? 0) - (b.original.pilMv ?? 0),
      },
      {
        accessorKey: 'sonPing',
        header: 'Son ping',
        meta: { etiket: 'Son ping' },
        cell: ({ row }) => (
          <span className="text-fume-500 text-xs">
            {row.original.sonPing
              ? new Date(row.original.sonPing).toLocaleString('tr-TR', {
                  day: 'numeric',
                  month: 'short',
                  hour: '2-digit',
                  minute: '2-digit',
                })
              : '—'}
          </span>
        ),
      },
    ],
    [seciliIdler, hepsiSecili, hicSecili, dolaplar],
  );

  const seciliSayisi = seciliIdler.size;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-fume-500 dark:text-fume-400 text-sm">
          {liste.data ? `${liste.data.toplam} dolap kayıtlı` : '...'}
          {seciliSayisi > 0 && (
            <span className="text-marka-600 dark:text-marka-400 ml-2 font-semibold">
              · {seciliSayisi} seçili
            </span>
          )}
        </p>
        <div className="flex flex-wrap gap-2">
          {seciliSayisi > 0 && (
            <Buton boyut="kucuk" stil="cizgili" onClick={() => setSeciliIdler(new Set())}>
              Seçimi Temizle
            </Buton>
          )}
          <Buton
            boyut="kucuk"
            stil="cizgili"
            disabled={seciliSayisi === 0}
            onClick={() => setDuzenleAcik(true)}
          >
            <SlidersHorizontal size={13} /> Toplu Düzenle
            {seciliSayisi > 0 ? ` (${seciliSayisi})` : ''}
          </Buton>
          <Buton boyut="kucuk" stil="cizgili" onClick={() => setTopluAcik(true)}>
            <Boxes size={13} /> Toplu Üret
          </Buton>
          <Buton boyut="kucuk" onClick={() => setYeniAcik(true)}>
            <Plus size={13} /> Yeni Dolap
          </Buton>
        </div>
      </div>

      <VeriTablo
        veri={dolaplar}
        kolonlar={kolonlar}
        toplam={liste.data?.toplam}
        sayfa={sayfa}
        boyut={50}
        onSayfa={setSayfa}
        yukleniyor={liste.isLoading}
        yenileniyor={liste.isFetching}
        onYenile={() => void liste.refetch()}
        onSatirTikla={(d) => dolabaTikla(d.id)}
        varsayilanGizliKolonlar={{ cihazUid: false }}
        bosDurum={
          <BosDurum
            ikon={Box}
            baslik={durum ? 'Bu durumda dolap yok' : 'Bu noktada henüz dolap yok'}
            aciklama={
              durum
                ? 'Durum filtresini değiştirip tekrar deneyin.'
                : 'İlk dolabı ekleyerek başlayın.'
            }
            aksiyon={
              !durum ? (
                <Buton boyut="kucuk" onClick={() => setYeniAcik(true)}>
                  <Plus size={13} /> Yeni Dolap
                </Buton>
              ) : undefined
            }
          />
        }
        ustAksiyonlar={
          <>
            <Filter size={13} className="text-fume-400" />
            <select
              value={durum}
              onChange={(e) => {
                setDurum(e.target.value as DolapDurumu | '');
                setSayfa(1);
              }}
              className="border-fume-200 focus:ring-marka-500 dark:border-fume-700 dark:bg-fume-900 dark:text-fume-50 h-10 rounded-lg border bg-white px-3 text-xs focus:outline-none focus:ring-2"
            >
              <option value="">Tüm durumlar</option>
              <option value="MUSAIT">Müsait</option>
              <option value="MESGUL">Meşgul</option>
              <option value="REZERVE">Rezerve</option>
              <option value="BAKIM">Bakım</option>
              <option value="CEVRIMDISI">Çevrimdışı</option>
            </select>
          </>
        }
      />

      <YeniDolapDialog
        acik={yeniAcik}
        onDegisim={setYeniAcik}
        noktaId={noktaId}
        noktaKod={noktaKod}
        noktaAd={noktaAd}
      />
      <TopluUretDialog
        acik={topluAcik}
        onDegisim={setTopluAcik}
        noktaId={noktaId}
        noktaKod={noktaKod}
        noktaAd={noktaAd}
      />
      <TopluDuzenleDialog
        acik={duzenleAcik}
        onDegisim={setDuzenleAcik}
        noktaId={noktaId}
        noktaKod={noktaKod}
        noktaAd={noktaAd}
        seciliIdler={Array.from(seciliIdler)}
        onTamamlandi={() => setSeciliIdler(new Set())}
      />
    </div>
  );
}

function PilGosterge({ mv }: { mv: number | null }) {
  if (mv === null) return <span className="text-fume-400 text-xs">—</span>;
  const yuzde = Math.max(0, Math.min(100, Math.round(((mv - 3300) / (4200 - 3300)) * 100)));
  const renk = yuzde > 50 ? 'bg-emerald-500' : yuzde > 20 ? 'bg-amber-500' : 'bg-red-500';
  return (
    <div className="flex items-center gap-2">
      <Battery size={13} className="text-fume-400" />
      <div className="bg-fume-200 dark:bg-fume-800 h-1.5 w-14 overflow-hidden rounded-full">
        <div className={`h-full ${renk}`} style={{ width: `${yuzde}%` }} />
      </div>
      <span className="text-fume-600 dark:text-fume-300 font-mono text-xs">{yuzde}%</span>
    </div>
  );
}
